export function groupArrayBy<T, K extends keyof T>(
  array: T[],
  property: K,
  groupCompareFn?: (a: T[K], b: T[K]) => number,
): T[][] {
  const groups: Record<string, T[]> = {}
  array.forEach((item) => {
    const key = String(item[property])
    if (!groups[key]) {
      groups[key] = []
    }
    groups[key].push(item)
  })
  const sortedKeys = groupCompareFn
    ? Object.keys(groups).sort(
        // since we know that the grouping happened based on unique values of K, we can safely use the first item as a comparison value
        (a, b) =>
          groupCompareFn(groups[a][0][property], groups[b][0][property]),
      )
    : Object.keys(groups)
  return sortedKeys.map((key) => groups[key])
}

export function mapEnumToValues<T extends Record<string, string | number>, U>(
  enumObj: T,
  values: U[],
): Record<T[keyof T], U> | undefined {
  const enumValues = getEnumValues(enumObj)

  if (enumValues.length !== values.length) {
    /* eslint-disable-next-line no-console */
    console.warn(
      'mapEnumToValues: Enum values and input values length mismatch',
    )
    return undefined
  }

  return enumValues.reduce(
    (acc, key, index) => {
      acc[key] = values[index]
      return acc
    },
    {} as Record<T[keyof T], U>,
  )
}

function getEnumValues<T extends object>(enumObj: T): Array<T[keyof T]> {
  const enumKeys = Object.keys(enumObj)
  const enumValues = new Set<T[keyof T]>()

  enumKeys.forEach((key) => {
    // ensure the key is not a number to filter out reverse mappings
    if (isNaN(Number(key))) {
      const value = enumObj[key as keyof T]
      enumValues.add(value)
    }
  })

  return Array.from(enumValues)
}
